/**
 * Widget formatting helpers.
 *
 * The standalone widget vite build has no `@shared` alias (see
 * `widget/vite.config.ts`: empty tsconfigRaw, no resolve.alias), but vite
 * bundles any resolvable RELATIVE import. The repo-root `shared/lib/*` modules
 * are framework-agnostic (no Nuxt/Vue deps), so the widget delegates to the
 * very same SSOT the SaaS uses — single implementation, single behaviour.
 */
import { formatMoney } from '../../../../../shared/lib/money'
import { formatPortTime, formatPortDate } from '../../../../../shared/lib/datetime'

export { humanizeLabel } from '../../../../../shared/lib/text'
export { normalizeBrandLogo } from '../../../../../shared/lib/cloudinary'

/**
 * Price + currency. Delegates to the money SSOT, which appends the (upper-cased)
 * currency code verbatim instead of `Intl.NumberFormat({ style: 'currency' })`
 * — the latter THROWS on the mixed-case codes the backend returns ("Eur"/"Mad").
 */
export function formatPrice(amount: number, currency: string, locale: string): string {
  return formatMoney(amount, currency, locale)
}

/**
 * Durée en MINUTES (champ `duration` provider — convention de stockage unique)
 * → "45 h" / "45 h 30". Format UNIQUE pour tout le funnel (SailingRow,
 * BookingSummary, Confirmation).
 */
export function formatDuration(min: number | null | undefined): string {
  if (typeof min !== 'number' || !Number.isFinite(min) || min <= 0) return ''
  const hours = Math.floor(min / 60)
  const minutes = Math.round(min % 60)
  if (hours === 0) return `${minutes} min`
  return minutes > 0 ? `${hours} h ${String(minutes).padStart(2, '0')}` : `${hours} h`
}

/**
 * Heure de port verbatim (HH:MM) sans conversion timezone. Délègue au SSOT
 * `formatPortTime` — qui gère exactement le "timezone-menteur" GNV
 * (`...+00:00` alors que l'heure est locale au port) en lisant les composants
 * de la string sans passer par `Date`/`Intl`. Préserve le fallback `'—'`.
 */
export function formatTime(iso: string | null | undefined, _locale: string): string {
  return formatPortTime(iso, '—')
}

/**
 * Date de port verbatim. Par défaut `DD/MM/YYYY`, identique à l'ancien
 * comportement (les call-sites n'utilisent jamais `options`). Délègue au SSOT
 * `formatPortDate`, shift-safe comme l'ancienne extraction regex + `T00:00:00Z`.
 */
export function formatDate(
  iso: string | null | undefined,
  _locale: string,
  _options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
): string {
  return formatPortDate(iso)
}

/** Nom de port capitalisé : "nador" → "Nador" (Results, SailingRow, Options, Confirmation). */
export function formatPort(name: string | null | undefined): string {
  if (!name) return ''
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
}

/** Nom de port en majuscules : "nador" → "NADOR" (résumé latéral BookingSummary). */
export function formatPortUpper(name: string | null | undefined): string {
  if (!name) return ''
  return name.toUpperCase()
}
